import { memo, useMemo, useState } from "react";
import {
  MiButton,
  MiModalConfirm,
  MiStackedTypography,
} from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  DATE_TIME_FORMATS,
  PERMISSIONS,
} from "@miplatform/constants";
import { MiTHEME } from "@miplatform/theme";
import { addWorkDays, formatDate, getDate, isBefore } from "@miplatform/utils";
import { mdiAlert, mdiThumbUpOutline } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { ORDERS_DEFAULT_FIELD_WIDTH } from "../../ordersConstants";
import { OrdersListActionMenu } from "../ActionMenu/OrdersListActionMenu";
import useConfirmedOrderActions from "./useConfirmedOrderActions";

/**
 * ConfirmedOrderActions Component
 *
 * A React functional component that displays the actions available for a confirmed order.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.id - The unique identifier for the order.
 * @param {string} props.approvedStartDate - The approved start date for the order.
 *
 * @returns {JSX.Element} The rendered ConfirmedOrderActions component.
 */
export const ConfirmedOrderActions = memo((props) => {
  const {
    id,
    approvedStartDate,
    confirmedScheduleDate,
    modifiedOn,
    onRefresh,
  } = props;
  const [openAssignWarningModal, setOpenAssignWarningModal] = useState(false);

  const { hasPermission } = usePermissions();

  const { isLoading, assignOrder } = useConfirmedOrderActions({
    id,
    modifiedOn,
    onRefresh,
  });

  const assignedDate = useMemo(() => {
    return addWorkDays(1, getDate()).hour(8).minute(0).second(0);
  }, []);

  const canAssignOrder = hasPermission(PERMISSIONS.ORDER.ASSIGN);
  const shouldWarnAssign = useMemo(() => {
    if (!confirmedScheduleDate) return false;
    return isBefore(assignedDate, getDate(confirmedScheduleDate));
  }, [confirmedScheduleDate]);

  const handleAssignClick = () => {
    if (shouldWarnAssign) {
      setOpenAssignWarningModal(true);
      return;
    }
    assignOrder();
  };

  return (
    <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
      <MiStackedTypography
        title="Approved Date"
        titleProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
        description={
          approvedStartDate
            ? formatDate(approvedStartDate, DATE_TIME_FORMATS.FORMAT_MM_DD_YY)
            : "--"
        }
        descriptionProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
      />
      <MiStackedTypography
        title="Confirmed Date"
        titleProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
        description={
          confirmedScheduleDate
            ? formatDate(
                confirmedScheduleDate,
                DATE_TIME_FORMATS.FORMAT_MM_DD_YY
              )
            : "--"
        }
        descriptionProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
      />
      {canAssignOrder && (
        <MiButton
          title="Assign"
          variant={BUTTON_VARIANTS.OUTLINED}
          disabled={isLoading}
          startIcon={{
            path: mdiThumbUpOutline,
            iconSize: 0.8,
          }}
          onClick={handleAssignClick}
        />
      )}
      <OrdersListActionMenu
        menuViewConfig={{
          Reschedule: true,
          Hold: true,
          Cancel: true,
          OrderActivityDetails: true,
          ForceComplete: true,
          UndoConfirmation: true,
        }}
        {...props}
      />
      <MiModalConfirm
        open={openAssignWarningModal}
        title="Warning"
        titleIcon={{
          path: mdiAlert,
          color: MiTHEME.palette.warning.main,
        }}
        description={`This order has been confirmed for ${formatDate(
          confirmedScheduleDate,
          DATE_TIME_FORMATS.FORMAT_MM_DD_YY
        )}, but will be assigned for ${formatDate(
          assignedDate,
          DATE_TIME_FORMATS.FORMAT_MM_DD_YY
        )}. Do you want to continue?`}
        textPositive="Assign"
        handleNegative={() => setOpenAssignWarningModal(false)}
        handlePositive={() => {
          setOpenAssignWarningModal(false);
          assignOrder();
        }}
      />
    </Box>
  );
});
